import React, { useMemo, useState } from 'react';
import { Location, Service } from '../../types';
import { EmptyState, Hint, SummaryRow } from '../UI/Bloom/flow';
import { T, rgba } from '../../utils/adminTheme';
import { supabase } from '../../lib/supabase';

interface TimeSelectionProps {
  selectedLocation: Location;
  selectedService: Service;
  selectedDate: Date;
  selectedTime: string | null;
  onTimeSelect: (time: string) => void;
  generateTimeSlots: () => string[];
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function timeToMin(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const GROUPS = [
  { label: 'Morgen',      from: 0,    to: 12 * 60 },
  { label: 'Mittag',      from: 12 * 60, to: 14 * 60 },
  { label: 'Nachmittag',  from: 14 * 60, to: 17 * 60 },
  { label: 'Abend',       from: 17 * 60, to: 24 * 60 },
];

// ─── Warteliste ────────────────────────────────────────────────────────────────
function WaitlistForm({ location, service, date, dateLabel }: {
  location: Location; service: Service; date: Date; dateLabel: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState<'form' | 'sending' | 'done' | 'error'>('form');
  const [errMsg, setErrMsg] = useState('');

  const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep('sending');
    try {
      const { error } = await supabase.from('waitlist').insert({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        location_id: location.id,
        service_id: service.id,
        preferred_date: iso,
      });
      if (error) throw error;
      setStep('done');
    } catch (err: any) {
      setErrMsg(err?.message || 'Fehler beim Speichern.');
      setStep('error');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    background: T.surface2, border: `1px solid ${T.line}`,
    borderRadius: T.r.md, fontFamily: T.body, fontSize: 14,
    color: T.text, outline: 'none', boxSizing: 'border-box',
  };

  if (step === 'done') {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ background: rgba('#2d7a52', 0.08), border: `1px solid ${rgba('#2d7a52', 0.25)}`, borderRadius: T.r.lg, padding: '24px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          <div style={{ fontFamily: T.body, fontSize: 15, fontWeight: 600, color: '#2d7a52', marginBottom: 6 }}>Auf der Warteliste!</div>
          <div style={{ fontFamily: T.body, fontSize: 13, color: T.muted }}>
            Wir melden uns, sobald ein Termin für den {dateLabel} frei wird.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <EmptyState title="Keine Zeiten verfügbar" hint={`Für ${dateLabel} sind alle Zeiten ausgebucht.`} />

      <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.lg, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', background: T.surface2, borderBottom: `1px solid ${T.line}` }}>
          <div style={{ fontFamily: T.body, fontSize: 13, fontWeight: 600, color: T.text }}>Auf Warteliste eintragen</div>
          <div style={{ fontFamily: T.body, fontSize: 12, color: T.muted, marginTop: 2 }}>
            Wir benachrichtigen Sie, wenn ein Platz frei wird.
          </div>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '16px', display: 'grid', gap: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ihr Name" required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>E-Mail *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ihre@email.de" required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Telefon (optional)</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+49 123 456789" style={inputStyle} />
          </div>
          {step === 'error' && (
            <div style={{ padding: '8px 12px', background: rgba('#ef4444', 0.08), color: '#dc2626', borderRadius: T.r.sm, fontSize: 12 }}>{errMsg}</div>
          )}
          <button
            type="submit"
            disabled={step === 'sending'}
            style={{ padding: '11px', background: T.accent, border: 'none', borderRadius: T.r.md, fontFamily: T.body, fontSize: 14, fontWeight: 600, color: '#fff', cursor: step === 'sending' ? 'not-allowed' : 'pointer', opacity: step === 'sending' ? 0.7 : 1 }}
          >
            {step === 'sending' ? 'Wird gespeichert…' : 'In Warteliste eintragen'}
          </button>
        </form>
      </div>
    </div>
  );
}

export const TimeSelection: React.FC<TimeSelectionProps> = ({
  selectedLocation,
  selectedService,
  selectedDate,
  selectedTime,
  onTimeSelect,
  generateTimeSlots,
}) => {
  const timeSlots = generateTimeSlots();

  const grouped = useMemo(() => {
    return GROUPS.map(g => ({
      label: g.label,
      slots: timeSlots.filter(t => {
        const m = timeToMin(t);
        return m >= g.from && m < g.to;
      }),
    })).filter(g => g.slots.length > 0);
  }, [timeSlots]);

  const dateLabel = selectedDate.toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  if (timeSlots.length === 0) {
    return (
      <WaitlistForm
        location={selectedLocation}
        service={selectedService}
        date={selectedDate}
        dateLabel={dateLabel}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Date label */}
      <div style={{
        fontSize: 12, color: T.muted, fontFamily: T.body, fontWeight: 500,
        textAlign: 'center', letterSpacing: '0.04em',
      }}>
        {dateLabel}
      </div>

      {/* Grouped slots */}
      <div style={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: T.r.lg,
        overflow: 'hidden',
      }}>
        {grouped.map((group, gi) => (
          <div key={group.label} style={{
            borderTop: gi > 0 ? `1px solid ${T.lineSoft}` : 'none',
            padding: '14px 16px',
          }}>
            <div style={{
              fontSize: 10, color: T.muted, fontFamily: T.body, fontWeight: 600,
              letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
            }}>
              {group.label}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 8,
            }}>
              {group.slots.map(time => {
                const active = selectedTime === time;
                const endTime = addMinutes(time, selectedService.duration);
                return (
                  <button
                    key={time}
                    onClick={() => onTimeSelect(time)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: T.r.md,
                      background: active ? T.accent : T.surface2,
                      color: active ? '#fff' : T.text,
                      border: `1px solid ${active ? T.accent : T.line}`,
                      cursor: 'pointer',
                      fontFamily: T.body,
                      textAlign: 'left',
                      boxShadow: active ? `0 8px 20px ${rgba(T.accent, 0.22)}` : 'none',
                      transition: 'all .14s',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{time}</div>
                    <div style={{
                      fontSize: 10, marginTop: 2,
                      color: active ? rgba('#fff', 0.75) : T.muted,
                      fontFamily: T.mono,
                    }}>
                      bis {endTime}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: T.r.lg,
        padding: 18,
        display: 'grid',
        gap: 11,
      }}>
        <SummaryRow k="Behandlung" v={selectedService.name} />
        <SummaryRow k="Dauer" v={`${selectedService.duration} Minuten`} />
        <SummaryRow k="Standort" v={selectedLocation.name} />
        <SummaryRow
          k="Datum"
          v={dateLabel}
          bold
        />
        {selectedTime && (
          <SummaryRow
            k="Uhrzeit"
            v={`${selectedTime} – ${addMinutes(selectedTime, selectedService.duration)} Uhr`}
            bold
          />
        )}
      </div>

      <Hint>
        Slots passen sich der Behandlungsdauer an — keine fixen Abstände.
      </Hint>
    </div>
  );
};
